import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTasks } from '../hooks/useTasks';
import { usePermissions } from '../hooks/usePermissions';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { useCustomers } from '../hooks/useCustomers';
import { useUsers } from '../hooks/useUsers';
import { Task, CreateTaskData, TaskStats } from '../types/task';
import { db } from '../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { actorStamp } from '../lib/audit';
import CommonPageHeader from '../components/common/CommonPageHeader';
import TaskForm from '../components/tasks/TaskForm';
import TaskListView from '../components/tasks/views/TaskListView';
import TaskBoard from '../components/tasks/views/TaskBoard';
import TaskCalendar from '../components/tasks/views/TaskCalendar';
import MyWork from '../components/tasks/views/MyWork';
import TaskReports from '../components/tasks/views/TaskReports';
import TaskView from '../components/tasks/TaskView';
import TaskCommentsModal from '../components/tasks/TaskCommentsModal';
import BulkTaskCreator, { BulkTaskData } from '../components/tasks/BulkTaskCreator';
import MonthlyReportModal from '../components/tasks/MonthlyReportModal';
import RecurringTasksModal from '../components/tasks/RecurringTasksModal';
import GlobalTimeTracker from '../components/layout/GlobalTimeTracker';
import SearchableSelect from '../components/common/SearchableSelect';
import LoadingScreen from '../components/common/LoadingScreen';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  ChartBarIcon,
  UserIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  XMarkIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  BoltIcon,
  DocumentChartBarIcon,
  ListBulletIcon,
  ViewColumnsIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';

const Tasks: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const { projects } = useProjects();
  const { customers } = useCustomers();
  const { users } = useUsers();
  const {
    tasks,
    loading,
    createTask,
    updateTask,
    deleteTask,
    updateTaskStatus,
    updateTaskPriority,
    assignTask,
    unassignTask,
    getTaskStats,
    getMyTasks,
    addComment,
    deleteComment,
    updateComment,
    deleteCommentAttachment,
    exportTaskAsText,
    downloadTasksAsText,
    recurringTemplates
  } = useTasks();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBulkCreatorOpen, setIsBulkCreatorOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isRecurringOpen, setIsRecurringOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  const [viewingTask, setViewingTask] = useState<Task | undefined>(undefined);
  // Mélylink: /tasks?task=<id> → a feladat nézet megnyílik (értesítés, napló, idővonal)
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get('task');
  useEffect(() => {
    if (!deepLinkId || loading) return;
    const found = tasks.find(x => x.id === deepLinkId);
    if (found) setViewingTask(found);
    else toast.error('A feladat nem található (törölve lett, vagy nincs hozzá jogosultság)');
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('task'); return n; }, { replace: true });
  }, [deepLinkId, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const [commentingTask, setCommentingTask] = useState<Task | undefined>(undefined);
  type TaskViewMode = 'list' | 'board' | 'calendar' | 'my' | 'stats';
  const [activeTab, setActiveTabState] = useState<TaskViewMode>(() => {
    try {
      const saved = localStorage.getItem('tasks.viewMode');
      if (saved === 'list' || saved === 'board' || saved === 'calendar' || saved === 'my' || saved === 'stats') return saved;
      if (saved === 'all') return 'list';
    } catch { /* ignore */ }
    return 'list';
  });
  const setActiveTab = (v: TaskViewMode) => { setActiveTabState(v); try { localStorage.setItem('tasks.viewMode', v); } catch { /* ignore */ } };
  const [quickStatus, setQuickStatus] = useState<'all' | 'todo' | 'in_progress' | 'completed' | 'overdue'>('all');
  
  // Szűrők
  const [filterProject, setFilterProject] = useState<string>('');
  const [filterCustomer, setFilterCustomer] = useState<string>('');
  const [filterAssignee, setFilterAssignee] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Effect to handle FAB trigger for new task
  useEffect(() => {
    const handleFABNewTask = () => {
      setSelectedTask(undefined);
      setIsFormOpen(true);
    };

    window.addEventListener('openTaskForm', handleFABNewTask);
    return () => {
      window.removeEventListener('openTaskForm', handleFABNewTask);
    };
  }, []);

  // Frissítjük a commentingTask-ot amikor a tasks változik
  useEffect(() => {
    if (commentingTask) {
      const updatedTask = tasks.find(t => t.id === commentingTask.id);
      if (updatedTask) {
        setCommentingTask(updatedTask);
      }
    }
  }, [tasks, commentingTask]);

  // Frissítjük a viewingTask-ot amikor a tasks változik
  useEffect(() => {
    if (viewingTask) {
      const updatedTask = tasks.find(t => t.id === viewingTask.id);
      if (updatedTask) {
        setViewingTask(updatedTask);
      }
    }
  }, [tasks, viewingTask]);

  // Feladat statisztikák - hooks mindig futnak
  const taskStats = useMemo(() => getTaskStats(), [getTaskStats]);
  const myTasks = useMemo(() => getMyTasks(), [getMyTasks]);
  const projectNames = useMemo(() => Object.fromEntries((projects || []).map(p => [p.id, p.name])), [projects]);

  // Szűrt task lista
  const filteredTasks = useMemo(() => {
    let filtered = activeTab === 'my' ? myTasks : tasks;

    if (quickStatus === 'overdue') {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      filtered = filtered.filter(task => task.dueDate && new Date(task.dueDate as any) < today && task.status !== 'completed' && task.status !== 'cancelled');
    } else if (quickStatus !== 'all') {
      filtered = filtered.filter(task => task.status === quickStatus);
    }

    if (filterProject) {
      filtered = filtered.filter(task => task.projectId === filterProject);
    }

    if (filterCustomer) {
      filtered = filtered.filter(task => task.customerId === filterCustomer);
    }

    if (filterAssignee) {
      filtered = filtered.filter(task => task.assignedTo === filterAssignee);
    }

    return filtered;
  }, [tasks, myTasks, activeTab, quickStatus, filterProject, filterCustomer, filterAssignee]);

  // Jogosultság ellenőrzés
  if (!hasAccess('tasks')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.loadingFailed')}</div>
      </div>
    );
  }

  // Feladat létrehozása
  const handleCreateTask = async (data: CreateTaskData) => {
    try {
      await createTask(data);
      toast.success(t('tasks.messages.createSuccess'));
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(t('tasks.messages.createError'));
    }
  };

  // Feladat frissítése
  const handleUpdateTask = async (data: CreateTaskData) => {
    if (!selectedTask) return;
    
    try {
      await updateTask(selectedTask.id, data);
      toast.success(t('tasks.messages.updateSuccess'));
      setSelectedTask(undefined);
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(t('tasks.messages.updateError'));
    }
  };

  // Feladat törlése
  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      toast.success(t('tasks.messages.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(t('tasks.messages.deleteError'));
    }
  };

  // Összes task kommentjeinek exportálása (szűrt lista alapján)
  const handleExportAllComments = () => {
    try {
      // Cím generálása a szűrők alapján
      let title = 'Feladat Kommentek';
      const filters: string[] = [];
      
      if (filterProject) {
        const project = projects.find(p => p.id === filterProject);
        if (project) filters.push(`Projekt: ${project.name}`);
      }
      if (filterCustomer) {
        const customer = customers.find(c => c.id === filterCustomer);
        if (customer) filters.push(`Ügyfél: ${customer.companyName}`);
      }
      if (filterAssignee) {
        const user = users.find(u => u.id === filterAssignee);
        if (user) filters.push(`Felelős: ${user.displayName || user.email}`);
      }
      
      let content = `${title}\n`;
      if (filters.length > 0) {
        content += `Szűrők: ${filters.join(', ')}\n`;
      }
      content += `Exportálva: ${new Date().toLocaleString('hu-HU')}\n`;
      content += `${'='.repeat(80)}\n\n`;

      // Szűrt task lista használata
      const tasksWithComments = filteredTasks.filter(task => task.comments && task.comments.length > 0);
      
      if (tasksWithComments.length === 0) {
        toast.error('Nincsenek kommentek az exportáláshoz');
        return;
      }

      tasksWithComments.forEach((task, index) => {
        content += `${index + 1}. ${task.taskNumber || 'N/A'} - ${task.title}\n`;
        content += `${'-'.repeat(80)}\n`;
        
        if (task.comments && task.comments.length > 0) {
          task.comments.forEach((comment, commentIndex) => {
            const date = comment.createdAt instanceof Date 
              ? comment.createdAt.toLocaleString('hu-HU')
              : new Date(comment.createdAt).toLocaleString('hu-HU');
            
            content += `\n[${commentIndex + 1}] ${comment.userName} - ${date}\n`;
            content += `${comment.message}\n`;
          });
        }
        
        content += `\n${'='.repeat(80)}\n\n`;
      });

      // Fájl letöltése
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Fájlnév generálása szűrők alapján
      let filename = 'task_kommentek';
      if (filterProject) {
        const project = projects.find(p => p.id === filterProject);
        if (project) filename += `_${project.name.replace(/[^a-z0-9]/gi, '_')}`;
      }
      if (filterCustomer) {
        const customer = customers.find(c => c.id === filterCustomer);
        if (customer) filename += `_${customer.companyName.replace(/[^a-z0-9]/gi, '_')}`;
      }
      filename += `_${new Date().toISOString().split('T')[0]}.txt`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const filterInfo = filters.length > 0 ? ` (${filters.join(', ')})` : '';
      toast.success(`${tasksWithComments.length} feladat kommentjei exportálva!${filterInfo}`);
    } catch (error) {
      console.error('Error exporting comments:', error);
      toast.error('Hiba történt az exportálás során');
    }
  };

  // Feladat státusz frissítése
  const handleUpdateStatus = async (taskId: string, status: Task['status'], statusId?: string) => {
    try {
      await updateTaskStatus(taskId, status, statusId);
      toast.success(t('tasks.messages.statusUpdateSuccess'));
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(t('tasks.messages.statusUpdateError'));
    }
  };

  // Feladat prioritás frissítése
  const handleUpdatePriority = async (taskId: string, priority: Task['priority']) => {
    try {
      await updateTaskPriority(taskId, priority);
      toast.success(t('tasks.messages.priorityUpdateSuccess'));
    } catch (error) {
      console.error('Error updating task priority:', error);
      toast.error(t('tasks.messages.priorityUpdateError'));
    }
  };

  // Feladat hozzárendelése (dropdown-ból)
  const handleAssign = async (taskId: string, userId: string, userName: string) => {
    try {
      await assignTask(taskId, userId, userName);
      toast.success(t('tasks.messages.assignSuccess') || 'Felelős sikeresen hozzárendelve');
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error(t('tasks.messages.assignError') || 'Hiba a felelős hozzárendelésekor');
    }
  };

  // Feladat hozzárendelése saját magunkhoz
  const handleAssignToMe = async (taskId: string) => {
    if (!user) return;
    
    try {
      await assignTask(taskId, user.id, user.displayName);
      toast.success(t('tasks.messages.assignSuccess') || 'Feladat hozzárendelve');
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error(t('tasks.messages.assignError') || 'Hiba a hozzárendeléskor');
    }
  };

  // Feladat hozzárendelés eltávolítása
  const handleUnassign = async (taskId: string) => {
    try {
      await unassignTask(taskId);
      toast.success(t('tasks.messages.unassignSuccess') || 'Felelős eltávolítva');
    } catch (error) {
      console.error('Error unassigning task:', error);
      toast.error(t('tasks.messages.assignError') || 'Hiba az eltávolításkor');
    }
  };

  // Határidő frissítése
  const handleUpdateDueDate = async (taskId: string, dueDate: Date | null) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        dueDate: dueDate || null,
        ...actorStamp()
      });
      toast.success('Határidő frissítve');
    } catch (error) {
      console.error('Error updating due date:', error);
      toast.error('Hiba a határidő frissítésekor');
    }
  };

  // Projekt frissítése
  const handleUpdateProject = async (taskId: string, projectId: string | null) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        projectId: projectId || null,
        ...actorStamp()
      });
      toast.success('Projekt frissítve');
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Hiba a projekt frissítésekor');
    }
  };

  // Becsült óraszám frissítése
  const handleUpdateEstimatedHours = async (taskId: string, hours: number | null) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        estimatedHours: hours,
        ...actorStamp()
      });
      toast.success('Becsült óraszám frissítve');
    } catch (error) {
      console.error('Error updating estimated hours:', error);
      toast.error('Hiba a becsült óraszám frissítésekor');
    }
  };

  // Manuális óraszám frissítése
  const handleUpdateManualHours = async (taskId: string, hours: number | null) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        manualHours: hours,
        ...actorStamp()
      });
      
      // Automatikusan újraszámoljuk az actualHours-t
      if (user?.organizationId) {
        const { updateTaskActualHours } = await import('../services/timeTrackingService');
        await updateTaskActualHours(taskId, user.organizationId);
      }
      
      toast.success(t('tasks.messages.updateSuccess') || 'Manuális órák frissítve');
    } catch (error) {
      console.error('Error updating manual hours:', error);
      toast.error(t('tasks.messages.updateError') || 'Hiba történt a manuális órák frissítése során');
    }
  };

  // Tényleges óraszám frissítése
  const handleUpdateActualHours = async (taskId: string, hours: number | null) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        actualHours: hours,
        ...actorStamp()
      });
      toast.success(t('tasks.messages.updateSuccess') || 'Feladat frissítve');
    } catch (error) {
      console.error('Error updating actual hours:', error);
      toast.error(t('tasks.messages.updateError') || 'Hiba történt a feladat frissítése során');
    }
  };

  // Feladat szerkesztése
  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setIsFormOpen(true);
  };

  // Feladat megtekintése
  const handleViewTask = (task: Task) => {
    setViewingTask(task);
  };

  // Kommentek megtekintése
  const handleViewComments = (task: Task) => {
    setCommentingTask(task);
  };

  // Form bezárása
  const handleCloseForm = () => {
    setSelectedTask(undefined);
    setIsFormOpen(false);
  };

  // View bezárása
  const handleCloseView = () => {
    setViewingTask(undefined);
  };

  // Comments modal bezárása
  const handleCloseComments = () => {
    setCommentingTask(undefined);
  };

  // Új feladat form
  const handleNewTask = () => {
    setSelectedTask(undefined);
    setIsFormOpen(true);
  };

  // Bulk task creator megnyitása
  const handleOpenBulkCreator = () => {
    setIsBulkCreatorOpen(true);
  };

  // Bulk task létrehozás
  const handleBulkCreateTasks = async (bulkTasks: BulkTaskData[]) => {
    try {
      const createdTasks: string[] = [];
      
      for (const bulkTask of bulkTasks) {
        const taskData: CreateTaskData = {
          title: bulkTask.title,
          description: bulkTask.description || '',
          dueDate: bulkTask.dueDate,
          estimatedHours: bulkTask.estimatedHours,
          priority: bulkTask.priority,
          status: bulkTask.status,
          projectId: bulkTask.projectId,
          customerId: bulkTask.customerId,
          assignedTo: bulkTask.assigneeId,
          assignedToName: bulkTask.assigneeName,
          tags: [],
          documents: [],
          subtasks: (bulkTask.subtasks || []).filter(Boolean).map(title => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, title, done: false, createdAt: new Date() }))
        };
        
        const createdTaskId = await createTask(taskData);
        
        // Ha van actualHours, akkor létrehozunk egy időbejegyzést
        if (bulkTask.actualHours && createdTaskId && user?.organizationId) {
          console.log('🕐 Időbejegyzés létrehozása:', {
            taskId: createdTaskId,
            actualHours: bulkTask.actualHours,
            title: bulkTask.title
          });
          
          const { addManualTimeEntry } = await import('../services/timeTrackingService');
          
          // Időbejegyzés létrehozása: ma 9:00-tól a megadott óraszámig
          const now = new Date();
          const startTime = new Date(now);
          startTime.setHours(9, 0, 0, 0); // Mai nap 9:00
          
          const endTime = new Date(startTime.getTime() + Math.round(bulkTask.actualHours * 3600000));
          
          console.log('⏱️ Időbejegyzés adatok:', {
            startTime: startTime.toLocaleString('hu-HU'),
            endTime: endTime.toLocaleString('hu-HU'),
            hours: bulkTask.actualHours
          });
          
          // Időbejegyzés létrehozása (ez automatikusan frissíti az actualHours-t)
          const timeEntry = await addManualTimeEntry(
            createdTaskId,
            user.id,
            user.displayName || user.email,
            user.organizationId,
            startTime,
            endTime,
            'Tömeges felvitel – előre rögzített idő',
            true // billable
          );
          
          console.log('✅ Időbejegyzés létrehozva:', timeEntry);
          
          // Kis várakozás, hogy a Firestore frissüljön
          await new Promise(resolve => setTimeout(resolve, 100));
        } else {
          console.log('⚠️ Nincs actualHours vagy hiányzó adat:', {
            hasActualHours: !!bulkTask.actualHours,
            actualHours: bulkTask.actualHours,
            hasTaskId: !!createdTaskId,
            hasOrgId: !!user?.organizationId
          });
        }
        
        createdTasks.push(bulkTask.title);
      }
      
      toast.success(`${createdTasks.length} feladat sikeresen létrehozva!`);
    } catch (error) {
      console.error('Error creating bulk tasks:', error);
      throw error;
    }
  };

  // Komment hozzáadása (ViewTask-hoz)
  const handleAddCommentView = async (message: string, files?: File[]) => {
    if (!viewingTask) return;
    await addComment(viewingTask.id, message, files);
  };

  // Komment törlése (ViewTask-hoz)
  const handleDeleteCommentView = async (commentId: string) => {
    if (!viewingTask) return;
    await deleteComment(viewingTask.id, commentId);
  };

  // Komment frissítése (ViewTask-hoz)
  const handleUpdateCommentView = async (commentId: string, message: string) => {
    if (!viewingTask) return;
    await updateComment(viewingTask.id, commentId, message);
  };

  // Komment melléklet törlése (ViewTask-hoz)
  const handleDeleteAttachmentView = async (commentId: string, attachmentId: string) => {
    if (!viewingTask) return;
    await deleteCommentAttachment(viewingTask.id, commentId, attachmentId);
  };

  // Komment hozzáadása (CommentsModal-hoz)
  const handleAddCommentModal = async (message: string, files?: File[]) => {
    if (!commentingTask) return;
    await addComment(commentingTask.id, message, files);
  };

  // Komment törlése (CommentsModal-hoz)
  const handleDeleteCommentModal = async (commentId: string) => {
    if (!commentingTask) return;
    await deleteComment(commentingTask.id, commentId);
  };

  // Komment frissítése (CommentsModal-hoz)
  const handleUpdateCommentModal = async (commentId: string, message: string) => {
    if (!commentingTask) return;
    await updateComment(commentingTask.id, commentId, message);
  };

  // Komment melléklet törlése (CommentsModal-hoz)
  const handleDeleteAttachmentModal = async (commentId: string, attachmentId: string) => {
    if (!commentingTask) return;
    await deleteCommentAttachment(commentingTask.id, commentId, attachmentId);
  };

  // Feladat exportálása
  const handleExportTask = () => {
    if (!viewingTask) return;
    downloadTasksAsText([viewingTask], `${viewingTask.taskNumber}_${viewingTask.title}.txt`);
    toast.success(t('tasks.messages.exportSuccess') || 'Feladat exportálva');
  };


  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-3 md:space-y-6">
      {/* Oldal fejléc */}
      <CommonPageHeader
        title={t('tasks.title')}
        subtitle={t('tasks.subtitle')}
        icon={ClipboardDocumentListIcon}
      >
        <div className="flex items-center gap-1.5 md:gap-2">
          <GlobalTimeTracker />
          <button
            onClick={() => setIsRecurringOpen(true)}
            className="hidden md:flex bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm items-center justify-center hover:bg-white/30 transition-colors"
            title="Ismétlődő feladatok / karbantartási ciklusok"
          >
            <ArrowPathIcon className="h-4 w-4 md:mr-2" />
            <span>Ismétlődő{recurringTemplates.length ? ` (${recurringTemplates.length})` : ''}</span>
          </button>
          <button
            onClick={() => setIsReportModalOpen(true)}
            className="hidden md:flex bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm items-center justify-center hover:bg-white/30 transition-colors"
            title="Havi kimutatás generálása"
          >
            <DocumentChartBarIcon className="h-4 w-4 md:mr-2" />
            <span>Riport</span>
          </button>
          <button
            onClick={handleExportAllComments}
            className="hidden lg:flex bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm items-center justify-center hover:bg-white/30 transition-colors"
            title="Összes komment exportálása"
          >
            <ArrowDownTrayIcon className="h-4 w-4 md:mr-2" />
            <span>Kommentek</span>
          </button>
          <button
            onClick={handleOpenBulkCreator}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
            title="Több feladat egyszerre"
          >
            <BoltIcon className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Több egyszerre</span>
          </button>
          <button
            onClick={handleNewTask}
            className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:from-primary-600 hover:to-primary-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{t('tasks.create')}</span>
          </button>
        </div>
      </CommonPageHeader>

      {/* Fő tartalom */}
      <div className="space-y-3 md:space-y-6">
        {/* KPI csempék + nézetváltó */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 flex-1">
            {([
              { key: 'all', label: 'Összes', n: taskStats.total },
              { key: 'todo', label: 'Teendő', n: taskStats.todo, dot: 'bg-gray-400' },
              { key: 'in_progress', label: 'Folyamatban', n: taskStats.inProgress, dot: 'bg-sky-500' },
              { key: 'completed', label: 'Kész', n: taskStats.completed, dot: 'bg-emerald-500' },
              { key: 'overdue', label: 'Lejárt', n: taskStats.overdue, dot: 'bg-rose-500' }
            ] as { key: typeof quickStatus; label: string; n: number; dot?: string }[]).map(tile => (
              <button
                key={tile.key}
                onClick={() => { setQuickStatus(tile.key); if (activeTab === 'stats') setActiveTab('list'); }}
                className={`crm-card px-3 py-2 text-left transition-colors ${quickStatus === tile.key && activeTab !== 'stats' ? 'border-gray-900 dark:border-white' : 'hover:border-gray-300 dark:hover:border-gray-600'}`}
              >
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 min-w-0">
                  {tile.dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${tile.dot}`} />}
                  <span className="truncate">{tile.label}</span>
                </div>
                <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50 leading-tight">{tile.n}</div>
              </button>
            ))}
          </div>
          <div className="flex items-center rounded-lg border border-[var(--crm-border)] bg-[var(--crm-surface)] p-0.5 flex-shrink-0 overflow-x-auto">
            {([
              { id: 'my', label: 'Nálam', icon: UserIcon, badge: myTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled').length },
              { id: 'list', label: 'Lista', icon: ListBulletIcon },
              { id: 'board', label: 'Tábla', icon: ViewColumnsIcon },
              { id: 'calendar', label: 'Naptár', icon: CalendarDaysIcon },
              { id: 'stats', label: 'Riport', icon: ChartBarIcon }
            ] as { id: TaskViewMode; label: string; icon: React.ElementType; badge?: number }[]).map(v => (
              <button
                key={v.id}
                onClick={() => setActiveTab(v.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-8 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${activeTab === v.id ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
              >
                <v.icon className="w-4 h-4" />{v.label}
                {v.badge ? <span className={`text-[10px] tabular-nums px-1 rounded ${activeTab === v.id ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700'}`}>{v.badge}</span> : null}
              </button>
            ))}
          </div>
        </div>

        {/* Tartalom a kiválasztott tab alapján */}
        {activeTab === 'stats' && (
          <TaskReports
            tasks={tasks}
            users={users as any}
            projects={projects as any}
            customers={customers as any}
            onOpenTask={(t) => setViewingTask(t)}
            onOpenMonthlyReport={() => setIsReportModalOpen(true)}
          />
        )}

        {/* Modern szűrő panel */}
        {activeTab !== 'stats' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            {/* Szűrő header */}
            <div className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-1 md:gap-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex-shrink-0"
                  >
                    <FunnelIcon className="h-4 w-4 md:h-5 md:w-5" />
                    <span className="text-xs md:text-sm font-medium">Szűrők</span>
                  </button>
                  
                  {/* Aktív szűrők badge-ek */}
                  <div className="flex items-center gap-1 md:gap-2 flex-wrap overflow-x-auto">
                    {filterProject && (
                      <span className="inline-flex items-center px-1.5 md:px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 whitespace-nowrap">
                        <BriefcaseIcon className="h-2.5 w-2.5 md:h-3 md:w-3 mr-0.5 md:mr-1" />
                        {projects.find(p => p.id === filterProject)?.name}
                        <button
                          onClick={() => setFilterProject('')}
                          className="ml-1 hover:text-blue-900 dark:hover:text-blue-200"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterCustomer && (
                      <span className="inline-flex items-center px-1.5 md:px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 whitespace-nowrap">
                        <BuildingOfficeIcon className="h-2.5 w-2.5 md:h-3 md:w-3 mr-0.5 md:mr-1" />
                        {customers.find(c => c.id === filterCustomer)?.companyName}
                        <button
                          onClick={() => setFilterCustomer('')}
                          className="ml-1 hover:text-green-900 dark:hover:text-green-200"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterAssignee && (
                      <span className="inline-flex items-center px-1.5 md:px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 whitespace-nowrap">
                        <UserIcon className="h-2.5 w-2.5 md:h-3 md:w-3 mr-0.5 md:mr-1" />
                        {users.find(u => u.id === filterAssignee)?.displayName || users.find(u => u.id === filterAssignee)?.email}
                        <button
                          onClick={() => setFilterAssignee('')}
                          className="ml-1 hover:text-purple-900 dark:hover:text-purple-200"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>

                {/* Összes szűrő törlése */}
                {(filterProject || filterCustomer || filterAssignee) && (
                  <button
                    onClick={() => {
                      setFilterProject('');
                      setFilterCustomer('');
                      setFilterAssignee('');
                    }}
                    className="text-xs md:text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    <span className="hidden md:inline">Összes törlése</span>
                    <span className="md:hidden">Törlés</span>
                  </button>
                )}
              </div>
            </div>

            {/* Összecsukható szűrő tartalom */}
            {showFilters && (
              <div className="px-2 md:px-4 py-2 md:py-4 bg-gray-50 dark:bg-gray-900/50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                  {/* Projekt szűrő */}
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <BriefcaseIcon className="h-4 w-4 mr-1.5 text-blue-600 dark:text-blue-400" />
                      Projekt
                    </label>
                    <SearchableSelect
                      options={[
                        { value: '', label: 'Összes projekt' },
                        ...projects.map(project => ({
                          value: project.id,
                          label: project.name,
                          icon: <BriefcaseIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        }))
                      ]}
                      value={filterProject}
                      onChange={setFilterProject}
                      placeholder="Válassz projektet..."
                      searchPlaceholder="Keresés projektek között..."
                      clearable
                    />
                  </div>

                  {/* Ügyfél szűrő */}
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <BuildingOfficeIcon className="h-4 w-4 mr-1.5 text-green-600 dark:text-green-400" />
                      Ügyfél
                    </label>
                    <SearchableSelect
                      options={[
                        { value: '', label: 'Összes ügyfél' },
                        ...customers.map(customer => ({
                          value: customer.id,
                          label: customer.companyName,
                          icon: <BuildingOfficeIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                        }))
                      ]}
                      value={filterCustomer}
                      onChange={setFilterCustomer}
                      placeholder="Válassz ügyfelet..."
                      searchPlaceholder="Keresés ügyfelek között..."
                      clearable
                    />
                  </div>

                  {/* Felelős szűrő */}
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <UserIcon className="h-4 w-4 mr-1.5 text-purple-600 dark:text-purple-400" />
                      Felelős
                    </label>
                    <SearchableSelect
                      options={[
                        { value: '', label: 'Összes felelős' },
                        ...users.map(user => ({
                          value: user.id,
                          label: user.displayName || user.email,
                          icon: <UserIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        }))
                      ]}
                      value={filterAssignee}
                      onChange={setFilterAssignee}
                      placeholder="Válassz felelőst..."
                      searchPlaceholder="Keresés felhasználók között..."
                      clearable
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Nálam – saját feladatok határidő szerint csoportosítva */}
        {activeTab === 'my' && (
          <MyWork tasks={filteredTasks} onView={handleViewTask} onUpdateStatus={handleUpdateStatus} projectNames={projectNames} />
        )}

        {/* Tábla nézet */}
        {activeTab === 'board' && (
          <TaskBoard tasks={filteredTasks} onView={handleViewTask} onUpdateStatus={handleUpdateStatus} onNew={() => handleNewTask()} projectNames={projectNames} />
        )}

        {/* Naptár nézet */}
        {activeTab === 'calendar' && (
          <TaskCalendar tasks={filteredTasks} onView={handleViewTask} onUpdateDueDate={handleUpdateDueDate} onNewOnDay={() => handleNewTask()} />
        )}

        {/* Feladatok listája */}
        {activeTab === 'list' && (
          <TaskListView
            tasks={filteredTasks}
            loading={loading}
            onView={handleViewTask}
            onEdit={handleEditTask}
            onDelete={handleDeleteTask}
            onUpdateStatus={handleUpdateStatus}
            onUpdatePriority={handleUpdatePriority}
            onAssign={handleAssign}
            onUnassign={handleUnassign}
            onUpdateDueDate={handleUpdateDueDate}
            onViewComments={handleViewComments}
            onUpdateSubtasks={async (taskId, subtasks) => { try { await updateTask(taskId, { subtasks }); } catch (e) { console.error(e); toast.error('Az alfeladatok mentése nem sikerült'); } }}
            onQuickCreate={async (title, status, statusId) => {
              try {
                await createTask({ title, status, statusId, priority: 'medium', tags: [], documents: [], assignedTo: user?.id, assignedToName: user?.displayName } as CreateTaskData);
              } catch (e) { console.error(e); toast.error(t('tasks.messages.createError')); }
            }}
          />
        )}
      </div>

      {/* Feladat form modal */}
      {isFormOpen && (
        <TaskForm
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onSubmit={selectedTask ? handleUpdateTask : handleCreateTask}
          initialData={selectedTask}
        />
      )}

      {/* Feladat view modal */}
      {viewingTask && (
        <TaskView
          task={viewingTask}
          isOpen={!!viewingTask}
          onClose={handleCloseView}
          onEdit={handleEditTask}
          onAddComment={handleAddCommentView}
          onDeleteComment={handleDeleteCommentView}
          onUpdateComment={handleUpdateCommentView}
          onDeleteAttachment={handleDeleteAttachmentView}
          onExportTask={handleExportTask}
          onUpdateStatus={handleUpdateStatus}
          onUpdatePriority={handleUpdatePriority}
          onUpdateSubtasks={async (taskId, subtasks) => { try { await updateTask(taskId, { subtasks }); } catch (e) { console.error(e); toast.error('Az alfeladatok mentése nem sikerült'); } }}
        />
      )}

      {/* Kommentek modal */}
      {commentingTask && (
        <TaskCommentsModal
          task={commentingTask}
          isOpen={!!commentingTask}
          onClose={handleCloseComments}
          onAddComment={handleAddCommentModal}
          onDeleteComment={handleDeleteCommentModal}
          onUpdateComment={handleUpdateCommentModal}
          onDeleteAttachment={handleDeleteAttachmentModal}
          onExportTask={() => {
            downloadTasksAsText([commentingTask], `${commentingTask.taskNumber}_${commentingTask.title}.txt`);
            toast.success(t('tasks.messages.exportSuccess') || 'Feladat exportálva');
          }}
        />
      )}

      {/* Bulk Task Creator modal */}
      <BulkTaskCreator
        isOpen={isBulkCreatorOpen}
        onClose={() => setIsBulkCreatorOpen(false)}
        onCreateTasks={handleBulkCreateTasks}
      />

      <RecurringTasksModal
        isOpen={isRecurringOpen}
        onClose={() => setIsRecurringOpen(false)}
        templates={recurringTemplates}
        onEdit={(tk) => { setIsRecurringOpen(false); handleEditTask(tk); }}
        onNew={() => { setIsRecurringOpen(false); handleNewTask(); toast('A második fülön kapcsold be az Ismétlődést', { icon: '↻' }); }}
        onToggleActive={async (tk, active) => { try { await updateTask(tk.id, { recurrence: { ...tk.recurrence!, active } }); } catch { toast.error('Nem sikerült menteni'); } }}
        onDelete={async (id) => { try { await deleteTask(id); toast.success('Sablon törölve'); } catch { toast.error('A törlés nem sikerült'); } }}
      />

      {/* Monthly Report modal */}
      <MonthlyReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
      />
    </div>
  );
};

export default Tasks;